import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES, scrollToId } from "@/lib/site";
import { Reveal, SectionLabel } from "@/components/primitives";

const EXAMPLES = {
  "web-dev": ["Design UI/UX", "Develop responsive", "Optimize speed", "Launch & convert"],
  "ai-video": ["Define concept", "Generate visuals", "Edit variations", "Publish & scale"],
  "ai-automation": ["Map workflow", "Connect tools", "Add AI logic", "Run on autopilot"],
};

const RESULT = {
  "web-dev": "More customers",
  "ai-video": "Higher engagement",
  "ai-automation": "Hours saved weekly",
};

export default function ServiceExperience() {
  const [active, setActive] = useState(SERVICES[0].id);
  const service = SERVICES.find((s) => s.id === active);

  return (
    <section className="relative bg-[#F8FAFC] py-20 sm:py-28" data-testid="service-experience-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <SectionLabel color="#7C3AED">Explore</SectionLabel>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#111827] sm:text-5xl">
            See how it works.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Pick a service to see the steps, what you get and the result it drives.
          </p>
        </Reveal>

        {/* selector */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            {SERVICES.map((s) => {
              const on = s.id === active;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`relative flex-1 overflow-hidden rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                    on ? "border-transparent bg-white shadow-card" : "border-slate-200 bg-white/50 hover:bg-white"
                  }`}
                  style={on ? { boxShadow: `0 8px 30px -8px ${service.accent}44` } : {}}
                  data-testid={`experience-tab-${s.id}`}
                  aria-pressed={on}
                >
                  {on && (
                    <motion.span layoutId="exp-bar" className="absolute inset-x-0 top-0 h-1" style={{ background: s.accent }} />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                    0{SERVICES.indexOf(s) + 1}
                  </span>
                  <span className="mt-1 block font-display text-lg font-bold text-[#111827]">{s.name}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* panel */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${active}-copy`}
              initial={{ opacity: 0, x: -14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 14 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl bg-white p-6 shadow-card ring-1 ring-slate-100 sm:p-8"
            >
              <h3 className="font-display text-2xl font-bold text-[#111827] sm:text-3xl">{service.title}</h3>
              <p className="mt-3 text-base text-slate-600">{service.description}</p>
              <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {service.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="grid h-5 w-5 place-items-center rounded-full" style={{ background: `${service.accent}1a` }}>
                      <Check className="h-3 w-3" style={{ color: service.accent }} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToId("contact")}
                className="group mt-8 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                style={{ background: `linear-gradient(90deg, ${service.accent}, ${service.accent2})` }}
                data-testid={`experience-cta-${active}`}
              >
                {service.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${active}-visual`}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex min-h-[320px] flex-col justify-between overflow-hidden rounded-3xl p-6 ring-1 ring-slate-100 sm:p-8"
              style={{ background: service.tint }}
            >
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-40 blur-2xl" style={{ background: service.accent }} />
              <div className="relative">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Example workflow</span>
                <div className="mt-5 space-y-2.5">
                  {EXAMPLES[active].map((step, i) => (
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.09 }}
                      className="flex items-center gap-3 rounded-2xl bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm"
                    >
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-xs font-bold text-white" style={{ background: service.accent }}>
                        {i + 1}
                      </span>
                      <span className="text-sm font-semibold text-[#111827]">{step}</span>
                      {i < EXAMPLES[active].length - 1 && <ArrowRight className="ml-auto h-4 w-4 text-slate-300" />}
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="relative mt-6 flex items-center justify-between rounded-2xl bg-[#111827] px-4 py-3 text-white">
                <span className="text-sm font-semibold">Result</span>
                <span className="font-display text-sm font-bold" style={{ color: service.accent2 === "#65A30D" ? "#B7F34A" : "#fff" }}>
                  {RESULT[active]}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
